// ATLAS Translations - French & English
// Premium, direct, strategic tone in both languages

export const translations = {
  fr: {
    // Splash
    splash: {
      tagline: "Amplification Cognitive Stratégique",
      initializing: "INITIALISATION",
    },

    // Landing
    landing: {
      nav: {
        product: "Produit",
        method: "Méthode",
        pricing: "Offre",
        login: "Se connecter",
        cta: "Activer ATLAS",
      },
      hero: {
        title: "Copilote Stratégique pour CEOs",
        subtitle: "Décidez mieux. Avancez plus vite. Gardez le contrôle.",
        description: "ATLAS n'est pas un outil. C'est un copilote stratégique qui t'aide à penser, décider et agir avec clarté — chaque jour.",
        cta: "Activer ATLAS",
        ctaSecondary: "Demander un accès privé",
      },
      problem: {
        title: "Tu ne manques pas d'opportunités.",
        titleAccent: "Tu manques de clarté au moment de décider.",
        items: {
          decisions: { title: "Trop de décisions", desc: "Chaque jour, tu arbitres entre des dizaines de choix sans avoir le recul nécessaire." },
          pressure: { title: "Pression constante", desc: "Le poids des décisions s'accumule. La fatigue décisionnelle tue la performance." },
          isolation: { title: "Isolement stratégique", desc: "Personne dans ton équipe ne peut challenger tes décisions au même niveau." },
          mistakes: { title: "Erreurs coûteuses", desc: "Une mauvaise décision peut coûter des mois de trésorerie et de momentum." },
        },
      },
      solution: {
        title: "ATLAS change ta façon d'opérer.",
        items: [
          "Structure tes décisions avec un cadre stratégique",
          "Analyse ta situation en temps réel (cash, leads, priorités)",
          "Évite les erreurs coûteuses avant qu'elles n'arrivent",
          "Avance avec clarté et rapidité"
        ],
      },
      howItWorks: {
        title: "Comment ça fonctionne",
        steps: {
          onboarding: { title: "Onboarding", desc: "ATLAS comprend ta situation — business, métriques, priorités." },
          daily: { title: "Support quotidien", desc: "Tu interagis avec ATLAS pour tes décisions et ta stratégie." },
          learning: { title: "Apprentissage continu", desc: "ATLAS apprend de ton comportement et s'améliore avec le temps." },
        },
      },
      experience: {
        title: "Trois modes. Une clarté constante.",
        modes: {
          morning: { label: "Matin", quote: "Voici où tu en es. Voici ce qui compte aujourd'hui." },
          decision: { label: "Décision", quote: "Voici tes options. Voici ce que je ferais." },
          evening: { label: "Soir", quote: "Voici ce que tu as appris. Voici comment progresser." },
        },
      },
      differentiation: {
        line1: "Pas un outil.",
        line2: "Pas un chatbot.",
        line3: "Un partenaire stratégique connecté à ta réalité.",
      },
      offer: {
        title: "ATLAS — Accès Anticipé",
        features: [
          "Copilote stratégique 24/7",
          "Mémoire personnalisée",
          "Dashboard business",
          "Briefings quotidiens",
          "Amélioration continue",
        ],
        price: "À partir de 497€/mois",
        priceNote: "Accès limité — Tarif early users (augmentera)",
        cta: "Activer ATLAS",
        ctaSecondary: "Demander un accès privé",
      },
      finalCta: {
        title: "Tu ne dois plus décider seul.",
        cta: "Activer ATLAS",
      },
      footer: {
        rights: "Tous droits réservés.",
        tagline: "Copilote Stratégique pour CEOs",
      },
    },

    // Payment
    payment: {
      verifying: "Vérification de ton paiement...",
      verifyingDesc: "Confirmation en cours...",
      success: "Paiement confirmé",
      almostThere: "Plus qu'une étape",
      createAccount: "Crée ton compte ATLAS",
      createAccountDesc: "Ton accès premium est activé. Configure ton compte pour commencer.",
      namePlaceholder: "Ton prénom",
      passwordPlaceholder: "Mot de passe (min. 6 caractères)",
      passwordLabel: "Mot de passe",
      passwordMin: "6 caractères minimum",
      cta: "Activer mon accès ATLAS",
      error: "Échec de la vérification du paiement. Contacte le support.",
      emailConfirmation: "Un email de confirmation vous sera envoyé",
    },

    // Access Request
    accessRequest: {
      title: "Demander un accès privé",
      subtitle: "Décris ton besoin et nous te recontacterons.",
      emailPlaceholder: "ton@email.com",
      namePlaceholder: "Ton nom complet",
      messagePlaceholder: "Parle-nous de ton business et tes besoins...",
      cta: "Envoyer ma demande",
      success: "Demande envoyée ! Nous te recontacterons très vite.",
      alreadyExists: "Cet email est déjà enregistré.",
    },

    // Access Guard
    accessGuard: {
      pending: {
        title: "Accès en attente",
        desc: "Ta demande est en cours de traitement. Tu recevras un accès sous peu.",
      },
      cancelled: {
        title: "Abonnement annulé",
        desc: "Ton abonnement a été annulé. Réactive-le pour accéder à ATLAS.",
      },
      rejected: {
        title: "Accès refusé",
        desc: "Ta demande d'accès n'a pas été acceptée. Contacte le support.",
      },
      cta: "Retour à l'accueil",
    },

    // Onboarding FR
    onboarding: {
      welcome: {
        title: "Bienvenue dans ATLAS",
        subtitle: "Votre copilote stratégique est prêt. Prenons 2 minutes pour calibrer ATLAS à votre activité.",
        feature1Title: "Analyse stratégique",
        feature1Desc: "ATLAS analyse votre situation et propose des actions concrètes",
        feature2Title: "Décisions rapides",
        feature2Desc: "Chaque session se termine par une décision claire à prendre",
        feature3Title: "Focus croissance",
        feature3Desc: "Suivi de vos KPIs, cash-flow et priorités en temps réel",
        cta: "Configurer ATLAS",
        time: "~ 2 minutes",
      },
      step1: {
        title: "Tu ne dois plus prendre tes décisions seul.",
        subtitle: "Je vais comprendre ta situation pour t'aider à avancer.",
        cta: "Commençons",
      },
      step2: {
        title: "Donne-moi un aperçu rapide de ta situation.",
        cashFlow: "Trésorerie actuelle (€)",
        revenue: "CA mensuel (€)",
        teamSize: "Taille de l'équipe",
        pressure: "Ton cash flow est-il sous pression ?",
        pressureLevels: {
          low: "Non, tout va bien",
          moderate: "Un peu, mais gérable",
          high: "Oui, c'est tendu",
          critical: "Situation critique",
        },
        cta: "Suivant",
      },
      step3: {
        title: "Quel est ton plus grand défi en ce moment ?",
        placeholder: "Décris ton challenge principal...",
        cta: "Analyser ma situation",
      },
      step4: {
        analyzing: "Analyse de ta situation en cours...",
        cta: "Commencer avec ATLAS",
      },
    },

    // Common
    common: {
      save: "Enregistrer",
      cancel: "Annuler",
      delete: "Supprimer",
      edit: "Modifier",
      loading: "Chargement...",
      error: "Une erreur est survenue",
      success: "Succès",
      confirm: "Confirmer",
      close: "Fermer",
    },
    
    // Auth
    auth: {
      login: "Connexion",
      register: "Créer un compte",
      logout: "Déconnexion",
      email: "Email",
      password: "Mot de passe",
      name: "Nom complet",
      signIn: "Se connecter",
      signUp: "S'inscrire",
      welcomeBack: "Bon retour",
      joinElite: "Rejoins l'élite stratégique",
      createAccount: "Créer un compte",
      noAccount: "Pas encore de compte ?",
      hasAccount: "Déjà un compte ?",
      createOne: "Créer un compte",
      signInLink: "Se connecter",
      language: "Langue",
      selectLanguage: "Choisis ta langue",
      businessContext: "Contexte Business (optionnel)",
      businessContextPlaceholder: "Décris ton business : secteur, rôle, défis...",
    },
    
    // Navigation
    nav: {
      dashboard: "Dashboard",
      atlas: "ATLAS",
      driving: "Vocal",
      drive: "Vocal",
      tracker: "Suivi",
      business: "Business",
      studio: "Studio",
      cfo: "CFO",
      team: "Équipe",
      profile: "Profil",
      admin: "Admin",
      copilot: "Copilote Stratégique",
      version: "v1.0.0 • Édition Premium",
      home: "Accueil",
      biz: "Biz",
    },
    
    // Dashboard
    dashboard: {
      greeting: {
        morning: "Bonjour",
        afternoon: "Bon après-midi", 
        evening: "Bonsoir",
      },
      startSession: "Démarrer une session stratégique",
      talkToAtlas: "Parler à ATLAS",
      updateMetrics: "Mettre à jour les métriques",
      stats: {
        pending: "En attente",
        completed: "Terminées",
        active: "Actifs",
        overdue: "En retard",
        decisions: "Décisions",
        commitments: "Engagements",
      },
      priorities: "Priorités actuelles",
      managePriorities: "Gérer les priorités",
      managePrioritiesHint: "Supprime manuellement les entrées que tu ne souhaites plus garder.",
      priorityDeleted: "Priorité supprimée",
      recentDecisions: "Décisions récentes",
      upcomingCommitments: "Engagements à venir",
      viewAll: "Voir tout",
      noPriorities: "Aucune priorité définie",
      noDecisions: "Aucune décision",
      noCommitments: "Aucun engagement",
      modeDescription: {
        morning: "Définis ta priorité #1 du jour",
        decision: "Résous un problème stratégique maintenant",
        evening: "Revue de ta journée et préparation de demain",
      },
      readyMessage: "Prêt à prendre des décisions stratégiques",
      businessMetricsLabels: {
        mrr: "MRR",
        cashFlow: "Trésorerie",
        runway: "Mois de Runway",
        activeLeads: "Leads Actifs",
      },
      modeName: {
        morning: "Matin",
        decision: "Décision",
        evening: "Soir",
      },
    },
    
    // Driving Mode
    driving: {
      modeLabel: "Mode Vocal",
      tapToSpeak: "Appuie pour parler à ATLAS",
      listening: "Je t'écoute...",
      autoSend: "Envoi automatique au silence",
      tapToStop: "Appuie pour envoyer",
      tapToSkip: "Appuie pour couper",
      processing: "Analyse en cours...",
      thinking: "ATLAS réfléchit...",
      atlasSpeak: "ATLAS parle...",
      loadingBriefing: "Briefing en cours...",
      briefingError: "Briefing indisponible",
      noSpeech: "Réessaie.",
      error: "Erreur.",
      micError: "Autorise le micro",
      conversationActive: "Conversation active",
    },

    // Chat
    chat: {
      newSession: "Nouvelle session",
      placeholder: "Écris ton message...",
      recording: "Enregistrement...",
      thinking: "ATLAS réfléchit...",
      listen: "Écouter",
      stop: "Stop",
      saveInsight: "Sauvegarder l'insight",
      suggestions: {
        morning: [
          "Quelle est ma priorité #1 aujourd'hui ?",
          "Qu'est-ce qui pourrait me bloquer ?",
          "Aide-moi à planifier mes 4 prochaines heures"
        ],
        decision: [
          "Je dois prendre une décision de pricing",
          "Comment gérer un concurrent moins cher ?",
          "Aide-moi à closer ce deal"
        ],
        evening: [
          "Qu'est-ce qui a bien fonctionné aujourd'hui ?",
          "Quelle a été ma plus grande perte de temps ?",
          "Aide-moi à préparer demain"
        ],
      },
      emptyState: {
        morning: "Définissons ta priorité #1 du jour.",
        decision: "Quel problème stratégique résolvons-nous ?",
        evening: "L'heure de la revue et de la préparation.",
      },
      inputHint: "Appuie sur Entrée pour envoyer, Shift+Entrée pour un retour à la ligne",
    },
    
    // Insights Modal
    insight: {
      title: "Sauvegarder l'insight",
      category: "Catégorie",
      content: "Contenu",
      categories: {
        decision: "⚡ Décision",
        learning: "💡 Apprentissage",
        pattern: "🔄 Pattern",
        goal: "🎯 Objectif",
        warning: "⚠️ Alerte",
      },
      saved: "Insight sauvegardé dans la mémoire !",
      failed: "Échec de la sauvegarde",
    },
    
    // Business
    business: {
      title: "Profil Business",
      subtitle: "Aide ATLAS à mieux comprendre ton business",
      saveAll: "Tout enregistrer",
      dataSaved: "Données sauvegardées !",
      saveFailed: "Échec de la sauvegarde",
      ceoProfile: "Profil CEO",
      businessMetrics: "Métriques Business",
      currentFocus: "Focus Actuel",
      runway: "Runway",
      months: "mois",
      runwayStatus: {
        critical: "Critique - Action immédiate requise",
        monitor: "À surveiller de près",
        healthy: "Runway sain",
      },
      fields: {
        companyName: "Nom de l'entreprise",
        industry: "Secteur",
        industryPlaceholder: "SaaS, E-commerce, Consulting...",
        companyStage: "Stade de l'entreprise",
        stages: {
          idea: "Idée / Pré-lancement",
          mvp: "MVP / Early stage",
          growth: "Croissance",
          scale: "Scale",
          mature: "Mature",
        },
        role: "Ton rôle",
        rolePlaceholder: "CEO, Fondateur, COO...",
        decisionStyle: "Style de décision",
        styles: {
          analytical: "Analytique (data-driven)",
          intuitive: "Intuitif (gut feeling)",
          collaborative: "Collaboratif (input équipe)",
          directive: "Directif (rapide & décisif)",
        },
        goalsQ1: "Objectifs Q1",
        goalsQ1Placeholder: "Objectif principal ce trimestre",
        monthlyRevenue: "CA Mensuel (€)",
        cashFlow: "Trésorerie (€)",
        burnRate: "Burn Rate (€/mois)",
        mrr: "MRR (€)",
        activeLeads: "Leads Actifs",
        activeClients: "Clients Actifs",
        teamSize: "Taille équipe",
        avgDealSize: "Panier moyen (€)",
        conversionRate: "Taux de conversion (%)",
        topChallenge: "Challenge principal",
        topChallengePlaceholder: "Qu'est-ce qui bloque ta croissance ?",
        currentFocus: "Focus stratégique actuel",
        currentFocusPlaceholder: "Sur quoi travailles-tu ce mois ?",
      },
      infoNote: {
        title: "Ces données alimentent l'intelligence d'ATLAS",
        description: "ATLAS utilise tes métriques business pour des conseils stratégiques personnalisés. Par exemple : \"Vu ta situation de trésorerie...\" ou \"Avec ton taux de conversion...\"",
      },
      selectStage: "Sélectionne le stade",
      howDecide: "Comment décides-tu ?",
    },
    
    // Profile
    profile: {
      title: "Profil",
      information: "Informations du profil",
      editProfile: "Modifier",
      fullName: "Nom complet",
      aboutAtlas: "À propos d'ATLAS",
      aboutDescription: "ATLAS est un système premium d'amplification cognitive stratégique conçu pour les entrepreneurs, CEOs et leaders business. Il sert de copilote stratégique d'élite, t'aidant à prendre des décisions plus rapides, à prioriser efficacement et à transformer les problèmes en leviers de croissance.",
      signOut: "Déconnexion",
      signOutConfirm: "Es-tu sûr de vouloir te déconnecter ?",
      version: "v1.0.0 • Amplification Cognitive Stratégique",
      modes: {
        morning: "Matin",
        morningDesc: "Focus priorités",
        decision: "Décision",
        decisionDesc: "Action stratégique",
        evening: "Soir",
        eveningDesc: "Revue quotidienne",
      },
      languageUpdated: "Langue mise à jour",
      notificationsRituals: "Notifications & Rituels",
      boussoleHour: "Heure de notification",
      boussoleHourDesc: "Lundi matin, entre 6h et 10h",
      debriefHour: "Heure de notification",
      debriefHourDesc: "Vendredi soir, entre 16h et 20h",
    },
    
    // Decisions/Tracker
    tracker: {
      title: "Suivi Stratégique",
      subtitle: "Gère tes décisions, engagements et priorités",
      tabs: {
        decisions: "Décisions",
        commitments: "Engagements",
        priorities: "Priorités",
      },
      addDecision: "Ajouter une décision",
      addCommitment: "Ajouter un engagement",
      addPriority: "Ajouter une priorité",
      noDecisions: "Aucune décision. Commence à faire des choix stratégiques !",
      noCommitments: "Aucun engagement. Fais une promesse à toi-même !",
      noPriorities: "Aucune priorité. Qu'est-ce qui compte le plus pour toi ?",
      priority: {
        low: "Basse",
        medium: "Moyenne",
        high: "Haute",
        critical: "Critique",
      },
      status: {
        pending: "En attente",
        completed: "Terminé",
        abandoned: "Abandonné",
        active: "Actif",
        missed: "Manqué",
      },
      overdue: "EN RETARD",
      deadline: "Échéance",
      pickDate: "Choisir une date",
      rank: "Rang",
      deleteConfirm: "Es-tu sûr de vouloir supprimer cet élément ?",
      add: "Ajouter",
      edit: "Modifier",
      titleField: "Titre",
      description: "Description",
      titlePlaceholder: "Entre le titre...",
      descriptionPlaceholder: "Entre la description...",
      due: "Échéance :",
      priorityLabel: "Priorité",
      statusLabel: "Statut",
    },
    
    // Alerts
    alerts: {
      cashFlow: "Alerte Trésorerie",
      cashFlowMsg: "Ton runway est de {months} mois. Envisage l'accélération des revenus ou la réduction des coûts.",
      overdueCommitments: "{count} Engagement(s) en retard",
      urgentDeadlines: "{count} Échéance(s) urgente(s)",
      actionNeeded: "Action requise",
      dueSoon: "Bientôt dû",
    },

    // Boussole du lundi
    boussole: {
      title: "La Boussole du lundi",
      subtitle: "3 minutes pour poser ta semaine",
      resumeSubtitle: "Tu as commencé ta Boussole. On la termine ?",
      start: "Commencer ma Boussole",
      resume: "Reprendre ma Boussole",
      drivingMode: "Mode vocal",
      skip: "Passer cette semaine",
      skipped: "Boussole passée pour cette semaine",
      completed: "Boussole complétée",
      thinking: "ATLAS réfléchit...",
      inputPlaceholder: "Ta réponse...",
      backToDashboard: "Retour au dashboard",
      cardSummaryTitle: "Ta Boussole de la semaine",
      yourPriorities: "Tes 3 priorités",
      decisionToMake: "Décision à trancher",
      settingsDesc: "Rituel hebdomadaire chaque lundi",
      sundayHint: "Tu peux poser ta semaine dès maintenant.",
      steps: {
        emotion: "Check-in",
        kpi: "KPI clé",
        priorities: "Priorités",
        decision: "Décision",
      },
    },

    // Débrief du vendredi
    debrief: {
      title: "Le Débrief du vendredi",
      subtitle: "5 minutes pour fermer ta semaine",
      resumeSubtitle: "Tu as commencé ton Débrief. On le termine ?",
      start: "Faire mon Débrief",
      resume: "Reprendre mon Débrief",
      drivingMode: "Mode vocal",
      skip: "Pas cette semaine",
      skipped: "Débrief passé pour cette semaine",
      completed: "Débrief complété",
      thinking: "ATLAS réfléchit...",
      inputPlaceholder: "Ta réponse...",
      backToDashboard: "Retour au dashboard",
      cardSummaryTitle: "Ton Débrief de la semaine",
      yourVictory: "Ta victoire",
      yourLearning: "Ton apprentissage",
      nothingToAdd: "Rien à poser",
      settingsDesc: "Rituel hebdomadaire chaque vendredi",
      steps: {
        victory: "Victoire",
        learning: "Leçon",
        openTopic: "Sujet ouvert",
        letGo: "Lâcher prise",
      },
    },

    // Journal des semaines
    journal: {
      title: "Mes semaines",
      subtitle: "Ton carnet de bord de dirigeant",
      empty: "Aucune semaine enregistrée. Commence par ta prochaine Boussole ou ton prochain Débrief.",
      weekOf: "Semaine du",
      prioritiesSet: "priorités posées",
      decisionMade: "décision tranchée",
      victory: "victoire",
      noActivity: "Aucune activité cette semaine",
      emotion: "Émotion",
      kpi: "KPI clé",
      priorities: "Priorités",
      decision: "Décision",
      victoryLabel: "Victoire",
      learningLabel: "Apprentissage",
      openTopicLabel: "Sujet ouvert",
      boussoleSkipped: "Boussole passée cette semaine",
      debriefSkipped: "Débrief passé cette semaine",
    },

    // Mode Capture
    capture: {
      title: "Capture rapide",
      subtitle: "Pose ce qui te trotte dans la tête. Je m'en occupe.",
      speakToAtlas: "Parler à ATLAS",
      write: "Écrire",
      listening: "Je t'écoute. Parle librement.",
      placeholder: "Parle-moi de cette idée, cette décision, ce sujet...",
      validate: "Valider",
      category: "Catégorie détectée par ATLAS",
      save: "Enregistrer",
      restart: "Recommencer",
      savedMessage: "Capturé.",
      savedSub: "Je te le ressors lundi matin dans ta Boussole.",
      micError: "Autorise le micro pour dicter",
      tooShort: "Enregistrement trop court",
      noSpeech: "Aucune parole détectée",
      sttError: "Erreur de transcription",
      journalTitle: "Journal des captures",
      journalSubtitle: "Tes idées, décisions et signaux capturés",
      emptyTitle: "Ton Journal attend ta première idée",
      emptyDesc: "Utilise le bouton éclair en bas à droite.",
      filterCategory: "Catégorie",
      filterStatus: "Statut",
      all: "Toutes",
      markTreated: "Traitée",
      archive: "Archiver",
      reopen: "Rouvrir",
      fabToggleLabel: "Bouton Capture",
      fabToggleDesc: "Bouton flottant sur toutes les pages",
      fabHiddenNote: "Le Mode Capture reste accessible depuis le Profil.",
      cat: {
        idee: "Idée",
        decision: "Décision à prendre",
        signal_rh: "Signal faible équipe / RH",
        opportunite: "Opportunité business",
        inquietude: "Inquiétude / sujet personnel",
        engagement: "Engagement à prendre",
        autre: "Autre",
      },
      status: {
        en_attente: "En attente",
        traitee: "Traitée",
        archivee: "Archivée",
      },
    },

    // Engagements
    engagements: {
      title: "Mes engagements",
      subtitle: "Tes blocs de temps et engagements stratégiques",
      today: "Aujourd\u2019hui",
      upcoming: "À venir",
      history: "Historique",
      new: "Nouvel engagement",
      edit: "Modifier l\u2019engagement",
      create: "Créer l\u2019engagement",
      save: "Enregistrer",
      created: "Engagement créé. ATLAS te le rappellera.",
      updated: "Engagement mis à jour.",
      statusUpdated: "Statut mis à jour.",
      whatCommitment: "À quoi tu t\u2019engages\u00a0?",
      descPlaceholder: "Ex\u00a0: Bloquer 2h pour bosser sur l\u2019acquisition",
      descTooShort: "Description trop courte (min 5 caractères)",
      missingFields: "Date et heure de début obligatoires",
      endBeforeStart: "L\u2019heure de fin doit être après l\u2019heure de début",
      date: "Date",
      startTime: "Heure de début",
      endTime: "Heure de fin (optionnel)",
      reminderToggle: "Activer le rappel ATLAS",
      reminderHint: "Tu recevras un rappel 3 min avant. Activable plus tard.",
      justifToggle: "Justification vocale si report",
      justifHint: "ATLAS te demandera pourquoi à voix haute.",
      markComplete: "Complété",
      postpone: "Reporter",
      modify: "Modifier",
      cancelEng: "Annuler",
      redoSimilar: "Refaire un similaire",
      todayWidget: "Mes engagements aujourd\u2019hui",
      viewAll: "Voir tout",
      noneToday: "Pas d\u2019engagement pour aujourd\u2019hui.",
      createOne: "Créer un engagement",
      emptyTitle: "Aucun engagement",
      emptyDesc: "Crée ton premier engagement avec le bouton + ci-dessous.",
    },
  },
  
  en: {
    // Splash
    splash: {
      tagline: "Strategic Cognitive Amplification",
      initializing: "INITIALIZING",
    },

    // Landing
    landing: {
      nav: {
        product: "Product",
        method: "Method",
        pricing: "Pricing",
        login: "Sign in",
        cta: "Activate ATLAS",
      },
      hero: {
        title: "Strategic Copilot for CEOs",
        subtitle: "Make better decisions. Move faster. Stay in control.",
        description: "ATLAS is not a tool. It's a strategic copilot that helps you think, decide, and act with clarity — every day.",
        cta: "Activate ATLAS",
        ctaSecondary: "Request Private Access",
      },
      problem: {
        title: "You don't lack opportunities.",
        titleAccent: "You lack clarity at the moment of decision.",
        items: {
          decisions: { title: "Too many decisions", desc: "Every day, you arbitrate between dozens of choices without the perspective you need." },
          pressure: { title: "Constant pressure", desc: "The weight of decisions accumulates. Decision fatigue kills performance." },
          isolation: { title: "Strategic isolation", desc: "No one on your team can challenge your decisions at the same level." },
          mistakes: { title: "Costly mistakes", desc: "One bad decision can cost months of cash flow and momentum." },
        },
      },
      solution: {
        title: "ATLAS changes how you operate.",
        items: [
          "Structure your decisions with a strategic framework",
          "Analyze your situation in real time (cash, leads, priorities)",
          "Avoid costly mistakes before they happen",
          "Move with clarity and speed"
        ],
      },
      howItWorks: {
        title: "How it works",
        steps: {
          onboarding: { title: "Onboarding", desc: "ATLAS understands your situation — business, metrics, priorities." },
          daily: { title: "Daily support", desc: "You interact with ATLAS for decisions and strategy." },
          learning: { title: "Continuous learning", desc: "ATLAS learns from your behavior and improves over time." },
        },
      },
      experience: {
        title: "Three modes. Constant clarity.",
        modes: {
          morning: { label: "Morning", quote: "Here is where you stand. Here is what matters today." },
          decision: { label: "Decision", quote: "Here are your options. Here is what I would do." },
          evening: { label: "Evening", quote: "Here is what you learned. Here is how to improve." },
        },
      },
      differentiation: {
        line1: "Not a tool.",
        line2: "Not a chatbot.",
        line3: "A strategic partner connected to your reality.",
      },
      offer: {
        title: "ATLAS — Early Access",
        features: [
          "Strategic copilot 24/7",
          "Personalized memory",
          "Business dashboard",
          "Daily briefings",
          "Continuous improvement",
        ],
        price: "From €497/month",
        priceNote: "Limited access — Early users pricing (will increase)",
        cta: "Activate ATLAS",
        ctaSecondary: "Request Private Access",
      },
      finalCta: {
        title: "You don't have to decide alone anymore.",
        cta: "Activate ATLAS",
      },
      footer: {
        rights: "All rights reserved.",
        tagline: "Strategic Copilot for CEOs",
      },
    },

    // Payment EN
    payment: {
      verifying: "Verifying your payment...",
      verifyingDesc: "Confirming your payment...",
      success: "Payment confirmed",
      almostThere: "One last step",
      createAccount: "Create your ATLAS account",
      createAccountDesc: "Your premium access is activated. Set up your account to get started.",
      namePlaceholder: "Your first name",
      passwordPlaceholder: "Password (min. 6 characters)",
      passwordLabel: "Password",
      passwordMin: "6 characters minimum",
      cta: "Activate my ATLAS access",
      error: "Payment verification failed. Contact support.",
      emailConfirmation: "A confirmation email will be sent to you",
    },

    // Access Request EN
    accessRequest: {
      title: "Request Private Access",
      subtitle: "Describe your needs and we'll get back to you.",
      emailPlaceholder: "your@email.com",
      namePlaceholder: "Your full name",
      messagePlaceholder: "Tell us about your business and needs...",
      cta: "Submit my request",
      success: "Request sent! We'll get back to you very soon.",
      alreadyExists: "This email is already registered.",
    },

    // Access Guard EN
    accessGuard: {
      pending: {
        title: "Access pending",
        desc: "Your request is being processed. You'll receive access shortly.",
      },
      cancelled: {
        title: "Subscription cancelled",
        desc: "Your subscription has been cancelled. Reactivate to access ATLAS.",
      },
      rejected: {
        title: "Access denied",
        desc: "Your access request was not accepted. Contact support.",
      },
      cta: "Back to home",
    },

    // Onboarding EN
    onboarding: {
      welcome: {
        title: "Welcome to ATLAS",
        subtitle: "Your strategic copilot is ready. Let's take 2 minutes to calibrate ATLAS to your business.",
        feature1Title: "Strategic Analysis",
        feature1Desc: "ATLAS analyzes your situation and proposes concrete actions",
        feature2Title: "Fast Decisions",
        feature2Desc: "Every session ends with a clear decision to make",
        feature3Title: "Growth Focus",
        feature3Desc: "Track your KPIs, cash flow, and priorities in real time",
        cta: "Set up ATLAS",
        time: "~ 2 minutes",
      },
      step1: {
        title: "You don't have to make decisions alone anymore.",
        subtitle: "I'm going to understand your situation to help you move forward.",
        cta: "Let's begin",
      },
      step2: {
        title: "Give me a quick snapshot of your situation.",
        cashFlow: "Current cash flow (€)",
        revenue: "Monthly revenue (€)",
        teamSize: "Team size",
        pressure: "Is your cash flow currently under pressure?",
        pressureLevels: {
          low: "No, all good",
          moderate: "A bit, but manageable",
          high: "Yes, it's tight",
          critical: "Critical situation",
        },
        cta: "Next",
      },
      step3: {
        title: "What is your #1 challenge right now?",
        placeholder: "Describe your main challenge...",
        cta: "Analyze my situation",
      },
      step4: {
        analyzing: "Analyzing your situation...",
        cta: "Start with ATLAS",
      },
    },

    // Common
    common: {
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      loading: "Loading...",
      error: "Something went wrong",
      success: "Success",
      confirm: "Confirm",
      close: "Close",
    },
    
    // Auth
    auth: {
      login: "Log In",
      register: "Sign Up",
      logout: "Log Out",
      email: "Email",
      password: "Password",
      name: "Full Name",
      signIn: "Sign In",
      signUp: "Sign Up",
      welcomeBack: "Welcome Back",
      joinElite: "Join the Strategic Elite",
      createAccount: "Create Account",
      noAccount: "Don't have an account?",
      hasAccount: "Already have an account?",
      createOne: "Create one",
      signInLink: "Sign in",
      language: "Language",
      selectLanguage: "Select your language",
      businessContext: "Business Context (optional)",
      businessContextPlaceholder: "Describe your business: industry, role, challenges...",
    },
    
    // Navigation
    nav: {
      dashboard: "Dashboard",
      atlas: "ATLAS",
      driving: "Voice",
      drive: "Voice",
      tracker: "Tracker",
      business: "Business",
      studio: "Studio",
      cfo: "CFO",
      team: "Team",
      profile: "Profile",
      admin: "Admin",
      copilot: "Strategic Copilot",
      version: "v1.0.0 • Premium Edition",
      home: "Home",
      biz: "Biz",
    },
    
    // Dashboard
    dashboard: {
      greeting: {
        morning: "Good morning",
        afternoon: "Good afternoon",
        evening: "Good evening",
      },
      startSession: "Start Strategic Session",
      talkToAtlas: "Talk to ATLAS",
      updateMetrics: "Update Metrics",
      stats: {
        pending: "Pending",
        completed: "Completed",
        active: "Active",
        overdue: "Overdue",
        decisions: "Decisions",
        commitments: "Commitments",
      },
      priorities: "Current Priorities",
      managePriorities: "Manage priorities",
      managePrioritiesHint: "Manually remove entries you no longer want to keep.",
      priorityDeleted: "Priority deleted",
      recentDecisions: "Recent Decisions",
      upcomingCommitments: "Upcoming Commitments",
      viewAll: "View All",
      noPriorities: "No priorities set yet",
      noDecisions: "No decisions yet",
      noCommitments: "No commitments yet",
      modeDescription: {
        morning: "Define your #1 priority for today",
        decision: "Solve a strategic problem now",
        evening: "Review your day and prepare tomorrow",
      },
      readyMessage: "Ready to make strategic decisions",
      businessMetricsLabels: {
        mrr: "MRR",
        cashFlow: "Cash Flow",
        runway: "Months Runway",
        activeLeads: "Active Leads",
      },
      modeName: {
        morning: "Morning",
        decision: "Decision",
        evening: "Evening",
      },
    },
    
    // Driving Mode EN
    driving: {
      modeLabel: "Voice Mode",
      tapToSpeak: "Tap to talk to ATLAS",
      listening: "Listening...",
      autoSend: "Auto-sends on silence",
      tapToStop: "Tap to send",
      tapToSkip: "Tap to skip",
      processing: "Processing...",
      thinking: "ATLAS is thinking...",
      atlasSpeak: "ATLAS speaking...",
      loadingBriefing: "Briefing loading...",
      briefingError: "Briefing unavailable",
      noSpeech: "Try again.",
      error: "Error.",
      micError: "Allow microphone",
      conversationActive: "Conversation active",
    },

    // Chat
    chat: {
      newSession: "New Session",
      placeholder: "Type your message...",
      recording: "Recording...",
      thinking: "ATLAS is thinking...",
      listen: "Listen",
      stop: "Stop",
      saveInsight: "Save Insight",
      suggestions: {
        morning: [
          "What's my #1 priority today?",
          "What could block me today?",
          "Help me plan the next 4 hours"
        ],
        decision: [
          "I need to make a pricing decision",
          "How do I handle a low-cost competitor?",
          "Help me close this deal"
        ],
        evening: [
          "What worked well today?",
          "What was my biggest time waste?",
          "Help me prepare tomorrow"
        ],
      },
      emptyState: {
        morning: "Let's define your #1 priority for today.",
        decision: "What strategic problem are we solving?",
        evening: "Time to review and prepare for tomorrow.",
      },
      inputHint: "Press Enter to send, Shift+Enter for new line",
    },
    
    // Insights Modal
    insight: {
      title: "Save Insight",
      category: "Category",
      content: "Content",
      categories: {
        decision: "⚡ Decision",
        learning: "💡 Learning",
        pattern: "🔄 Pattern",
        goal: "🎯 Goal",
        warning: "⚠️ Warning",
      },
      saved: "Insight saved to memory!",
      failed: "Failed to save insight",
    },
    
    // Business
    business: {
      title: "Business Profile",
      subtitle: "Help ATLAS understand your business better",
      saveAll: "Save All",
      dataSaved: "Data saved successfully!",
      saveFailed: "Failed to save data",
      ceoProfile: "CEO Profile",
      businessMetrics: "Business Metrics",
      currentFocus: "Current Focus",
      runway: "Runway",
      months: "months",
      runwayStatus: {
        critical: "Critical - Action needed immediately",
        monitor: "Monitor closely",
        healthy: "Healthy runway",
      },
      fields: {
        companyName: "Company Name",
        industry: "Industry",
        industryPlaceholder: "SaaS, E-commerce, Consulting...",
        companyStage: "Company Stage",
        stages: {
          idea: "Idea / Pre-launch",
          mvp: "MVP / Early stage",
          growth: "Growth",
          scale: "Scale",
          mature: "Mature",
        },
        role: "Your Role",
        rolePlaceholder: "CEO, Founder, COO...",
        decisionStyle: "Decision Style",
        styles: {
          analytical: "Analytical (data-driven)",
          intuitive: "Intuitive (gut feeling)",
          collaborative: "Collaborative (team input)",
          directive: "Directive (fast & decisive)",
        },
        goalsQ1: "Q1 Goals",
        goalsQ1Placeholder: "Main goal for this quarter",
        monthlyRevenue: "Monthly Revenue (€)",
        cashFlow: "Cash Flow / Treasury (€)",
        burnRate: "Burn Rate (€/month)",
        mrr: "MRR (€)",
        activeLeads: "Active Leads",
        activeClients: "Active Clients",
        teamSize: "Team Size",
        avgDealSize: "Avg Deal Size (€)",
        conversionRate: "Conversion Rate (%)",
        topChallenge: "Top Challenge Right Now",
        topChallengePlaceholder: "What's the #1 thing blocking your growth?",
        currentFocus: "Current Strategic Focus",
        currentFocusPlaceholder: "What are you working on this month?",
      },
      infoNote: {
        title: "This data powers ATLAS intelligence",
        description: "ATLAS uses your business metrics to provide personalized strategic advice. For example: \"Given your current cash situation...\" or \"Based on your conversion rate...\"",
      },
      selectStage: "Select stage",
      howDecide: "How do you decide?",
    },
    
    // Profile
    profile: {
      title: "Profile",
      information: "Profile Information",
      editProfile: "Edit",
      fullName: "Full Name",
      aboutAtlas: "About ATLAS",
      aboutDescription: "ATLAS is a premium strategic cognitive amplification system designed for entrepreneurs, CEOs, and business leaders. It serves as your elite strategic copilot, helping you make faster decisions, prioritize effectively, and transform problems into growth levers.",
      signOut: "Sign Out",
      signOutConfirm: "Are you sure you want to sign out?",
      version: "v1.0.0 • Strategic Cognitive Amplification",
      modes: {
        morning: "Morning",
        morningDesc: "Priority Focus",
        decision: "Decision",
        decisionDesc: "Strategic Action",
        evening: "Evening",
        eveningDesc: "Daily Review",
      },
      languageUpdated: "Language updated",
      notificationsRituals: "Notifications & Rituals",
      boussoleHour: "Notification time",
      boussoleHourDesc: "Monday morning, between 6am and 10am",
      debriefHour: "Notification time",
      debriefHourDesc: "Friday evening, between 4pm and 8pm",
    },
    
    // Decisions/Tracker
    tracker: {
      title: "Strategic Tracker",
      subtitle: "Manage your decisions, commitments, and priorities",
      tabs: {
        decisions: "Decisions",
        commitments: "Commitments",
        priorities: "Priorities",
      },
      addDecision: "Add Decision",
      addCommitment: "Add Commitment",
      addPriority: "Add Priority",
      noDecisions: "No decisions yet. Start making strategic choices!",
      noCommitments: "No commitments yet. Make a promise to yourself!",
      noPriorities: "No priorities set. What matters most to you?",
      priority: {
        low: "Low",
        medium: "Medium",
        high: "High",
        critical: "Critical",
      },
      status: {
        pending: "Pending",
        completed: "Completed",
        abandoned: "Abandoned",
        active: "Active",
        missed: "Missed",
      },
      overdue: "OVERDUE",
      deadline: "Deadline",
      pickDate: "Pick a date",
      rank: "Rank",
      deleteConfirm: "Are you sure you want to delete this item?",
      add: "Add",
      edit: "Edit",
      titleField: "Title",
      description: "Description",
      titlePlaceholder: "Enter title...",
      descriptionPlaceholder: "Enter description...",
      due: "Due:",
      priorityLabel: "Priority",
      statusLabel: "Status",
    },
    
    // Alerts
    alerts: {
      cashFlow: "Cash Flow Alert",
      cashFlowMsg: "Your runway is {months} months. Consider revenue acceleration or cost reduction.",
      overdueCommitments: "{count} Overdue Commitment(s)",
      urgentDeadlines: "{count} Urgent Deadline(s)",
      actionNeeded: "Action needed",
      dueSoon: "Due soon",
    },

    // Monday Compass
    boussole: {
      title: "Monday Compass",
      subtitle: "3 minutes to set your week",
      resumeSubtitle: "You started your Compass. Shall we finish?",
      start: "Start my Compass",
      resume: "Resume my Compass",
      drivingMode: "Voice mode",
      skip: "Skip this week",
      skipped: "Compass skipped for this week",
      completed: "Compass completed",
      thinking: "ATLAS is thinking...",
      inputPlaceholder: "Your answer...",
      backToDashboard: "Back to dashboard",
      cardSummaryTitle: "Your Weekly Compass",
      yourPriorities: "Your 3 priorities",
      decisionToMake: "Decision to make",
      settingsDesc: "Weekly ritual every Monday",
      sundayHint: "You can set your week right now.",
      steps: {
        emotion: "Check-in",
        kpi: "Key KPI",
        priorities: "Priorities",
        decision: "Decision",
      },
    },

    // Friday Debrief
    debrief: {
      title: "Friday Debrief",
      subtitle: "5 minutes to close your week",
      resumeSubtitle: "You started your Debrief. Shall we finish?",
      start: "Start my Debrief",
      resume: "Resume my Debrief",
      drivingMode: "Voice mode",
      skip: "Not this week",
      skipped: "Debrief skipped for this week",
      completed: "Debrief completed",
      thinking: "ATLAS is thinking...",
      inputPlaceholder: "Your answer...",
      backToDashboard: "Back to dashboard",
      cardSummaryTitle: "Your Weekly Debrief",
      yourVictory: "Your victory",
      yourLearning: "Your learning",
      nothingToAdd: "Nothing to add",
      settingsDesc: "Weekly ritual every Friday",
      steps: {
        victory: "Victory",
        learning: "Lesson",
        openTopic: "Open topic",
        letGo: "Let go",
      },
    },

    // Weekly Journal
    journal: {
      title: "My Weeks",
      subtitle: "Your leadership logbook",
      empty: "No weeks recorded yet. Start with your next Compass or Debrief.",
      weekOf: "Week of",
      prioritiesSet: "priorities set",
      decisionMade: "decision made",
      victory: "victory",
      noActivity: "No activity this week",
      emotion: "Emotion",
      kpi: "Key KPI",
      priorities: "Priorities",
      decision: "Decision",
      victoryLabel: "Victory",
      learningLabel: "Learning",
      openTopicLabel: "Open topic",
      boussoleSkipped: "Compass skipped this week",
      debriefSkipped: "Debrief skipped this week",
    },

    // Capture Mode
    capture: {
      title: "Quick Capture",
      subtitle: "Get it out of your head. I'll handle it.",
      speakToAtlas: "Speak to ATLAS",
      write: "Write",
      listening: "I'm listening. Speak freely.",
      placeholder: "Tell me about this idea, decision, topic...",
      validate: "Submit",
      category: "Category detected by ATLAS",
      save: "Save",
      restart: "Restart",
      savedMessage: "Captured.",
      savedSub: "I'll bring it up Monday morning in your Compass.",
      micError: "Allow microphone to dictate",
      tooShort: "Recording too short",
      noSpeech: "No speech detected",
      sttError: "Transcription error",
      journalTitle: "Capture Journal",
      journalSubtitle: "Your ideas, decisions and signals captured",
      emptyTitle: "Your Journal awaits its first idea",
      emptyDesc: "Use the lightning button at the bottom right.",
      filterCategory: "Category",
      filterStatus: "Status",
      all: "All",
      markTreated: "Treated",
      archive: "Archive",
      reopen: "Reopen",
      fabToggleLabel: "Capture Button",
      fabToggleDesc: "Floating button on all pages",
      fabHiddenNote: "Capture Mode remains accessible from Profile.",
      cat: {
        idee: "Idea",
        decision: "Decision to make",
        signal_rh: "Team / HR signal",
        opportunite: "Business opportunity",
        inquietude: "Concern / personal topic",
        engagement: "Commitment to schedule",
        autre: "Other",
      },
      status: {
        en_attente: "Pending",
        traitee: "Treated",
        archivee: "Archived",
      },
    },

    // Engagements
    engagements: {
      title: "My Commitments",
      subtitle: "Your time blocks and strategic commitments",
      today: "Today",
      upcoming: "Upcoming",
      history: "History",
      new: "New commitment",
      edit: "Edit commitment",
      create: "Create commitment",
      save: "Save",
      created: "Commitment created. ATLAS will remind you.",
      updated: "Commitment updated.",
      statusUpdated: "Status updated.",
      whatCommitment: "What are you committing to?",
      descPlaceholder: "E.g.: Block 2h to work on acquisition",
      descTooShort: "Description too short (min 5 characters)",
      missingFields: "Date and start time are required",
      endBeforeStart: "End time must be after start time",
      date: "Date",
      startTime: "Start time",
      endTime: "End time (optional)",
      reminderToggle: "Enable ATLAS reminder",
      reminderHint: "You'll get a reminder 3 min before. Can be enabled later.",
      justifToggle: "Voice justification if postponed",
      justifHint: "ATLAS will ask why out loud.",
      markComplete: "Completed",
      postpone: "Postpone",
      modify: "Edit",
      cancelEng: "Cancel",
      redoSimilar: "Redo similar",
      todayWidget: "My commitments today",
      viewAll: "View all",
      noneToday: "No commitment for today.",
      createOne: "Create a commitment",
      emptyTitle: "No commitments",
      emptyDesc: "Create your first commitment with the + button below.",
    },
  },
};

export const getTranslation = (lang, path) => {
  const keys = path.split('.');
  let value = translations[lang] || translations.fr;
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      // Fallback to French if key not found
      value = translations.fr;
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          return path; // Return path if not found
        }
      }
      break;
    }
  }
  
  return value;
};
